import { DisplayRule, PropertyMatchOperator } from '$/GraphQL/schema/types';
import { PropertyMatchOperator as PropertyMatchOperatorNames } from '$/shared/frontend/PropertyMatchOperator';
import { ActionPropertyValueType, DropdownOptionProps } from './types';

export abstract class MatchOperator {
  public type!: PropertyMatchOperator;
  public constructor(protected label?: string) {}

  abstract perform(displayRule: DisplayRule, value: ActionPropertyValueType): boolean;

  protected isNullOrEmpty(value: ActionPropertyValueType): boolean {
    if (value === undefined || value === null) return true;
    if (Array.isArray(value) && value.length === 0) return true;
    if (typeof value === 'string' && value.trim().length === 0) return true;
    return false;
  }

  protected isString(value: ActionPropertyValueType): boolean {
    return typeof value === 'string';
  }

  protected stringValueOf(value: ActionPropertyValueType): string | undefined {
    if (typeof value === 'number') return String(value);
    if (!this.isString(value)) return undefined;
    return (value as string).trim();
  }

  protected arrayValueOf(value: ActionPropertyValueType): string[] | undefined {
    if (Array.isArray(value)) return value as string[]; // for multiple option
    if (typeof value === 'string') return [value] as string[]; //  for built-in Selection fields
    if (typeof value === 'number') return [String(value)] as string[]; //  for built-in Selection fields
    return undefined;
  }

  public getOption(): DropdownOptionProps {
    return {
      text: this.label ?? PropertyMatchOperatorNames[this.type],
      key: this.type,
      value: this.type,
    };
  }
}

export class HasResponseOperator extends MatchOperator {
  public type = PropertyMatchOperator.HasResponse;
  public perform(_displayRule: DisplayRule, value: ActionPropertyValueType): boolean {
    return !this.isNullOrEmpty(value);
  }
}

export class HasNoResponseOperator extends MatchOperator {
  public type = PropertyMatchOperator.HasNoResponse;
  public perform(_displayRule: DisplayRule, value: ActionPropertyValueType): boolean {
    return this.isNullOrEmpty(value);
  }
}

class StartsWithOperator extends MatchOperator {
  public type = PropertyMatchOperator.StartsWith;
  public perform(displayRule: DisplayRule, value: ActionPropertyValueType): boolean {
    const stringValue = this.stringValueOf(value);
    if (!stringValue || !displayRule.value) return false;
    return stringValue.startsWith(displayRule.value);
  }
}

class EndsWithOperator extends MatchOperator {
  public type = PropertyMatchOperator.EndsWith;
  public perform(displayRule: DisplayRule, value: ActionPropertyValueType): boolean {
    const stringValue = this.stringValueOf(value);
    if (!stringValue || !displayRule.value) return false;
    return stringValue.endsWith(displayRule.value);
  }
}

class IsExactlyOperator extends MatchOperator {
  public type = PropertyMatchOperator.IsExactly;
  public perform(displayRule: DisplayRule, value: ActionPropertyValueType): boolean {
    const stringValue = this.stringValueOf(value);
    if (!stringValue || !displayRule.value) return false;
    return stringValue === displayRule.value;
  }
}

class ContainsOperator extends MatchOperator {
  public type = PropertyMatchOperator.Contains;
  public perform(displayRule: DisplayRule, value: ActionPropertyValueType): boolean {
    const stringValue = this.stringValueOf(value);
    if (!stringValue || !displayRule.value) return false;
    return stringValue.includes(displayRule.value);
  }
}

class NotContainOperator extends ContainsOperator {
  public type = PropertyMatchOperator.NotContain;
  public perform(displayRule: DisplayRule, value: ActionPropertyValueType): boolean {
    return !super.perform(displayRule, value);
  }
}

class IsAnyOfOperator extends MatchOperator {
  public type = PropertyMatchOperator.IsAnyOf;
  public perform(displayRule: DisplayRule, value: ActionPropertyValueType): boolean {
    const arrayValue = this.arrayValueOf(value);
    const ruleValues = displayRule.arrayValue;
    if (!value && ruleValues?.includes('Not answered')) return true; // `Not answered` from Quiz
    if (!arrayValue || !ruleValues?.length) return false;
    return arrayValue.some((v) => ruleValues.includes(v));
  }
}

class IsNoneOfOperator extends IsAnyOfOperator {
  public type = PropertyMatchOperator.IsNoneOf;
  public perform(displayRule: DisplayRule, value: ActionPropertyValueType): boolean {
    return !super.perform(displayRule, value);
  }
}

class AnsweredCorrectlyOperator extends MatchOperator {
  public type = PropertyMatchOperator.AnsweredCorrectly;
  public perform(_displayRule: DisplayRule, value: ActionPropertyValueType): boolean {
    return true === value || 'true' === value;
  }
}

class AnsweredIncorrectlyOperator extends MatchOperator {
  public type = PropertyMatchOperator.AnsweredIncorrectly;
  public perform(_displayRule: DisplayRule, value: ActionPropertyValueType): boolean {
    return false === value || 'false' === value || this.isNullOrEmpty(value);
  }
}

class NotAnsweredOperator extends MatchOperator {
  public type = PropertyMatchOperator.NotAnswered;
  public perform(_displayRule: DisplayRule, value: ActionPropertyValueType): boolean {
    return this.isNullOrEmpty(value);
  }
}

export class PropertyMatchOperatorFactory {
  public static instanceOf(operator: PropertyMatchOperator, label?: string): MatchOperator {
    switch (operator) {
      case PropertyMatchOperator.HasResponse:
        return new HasResponseOperator(label);
      case PropertyMatchOperator.HasNoResponse:
        return new HasNoResponseOperator(label);
      case PropertyMatchOperator.StartsWith:
        return new StartsWithOperator(label);
      case PropertyMatchOperator.EndsWith:
        return new EndsWithOperator(label);
      case PropertyMatchOperator.Contains:
        return new ContainsOperator(label);
      case PropertyMatchOperator.NotContain:
        return new NotContainOperator(label);
      case PropertyMatchOperator.IsExactly:
        return new IsExactlyOperator(label);
      case PropertyMatchOperator.IsAnyOf:
        return new IsAnyOfOperator(label);
      case PropertyMatchOperator.IsNoneOf:
        return new IsNoneOfOperator(label);
      case PropertyMatchOperator.AnsweredCorrectly:
        return new AnsweredCorrectlyOperator(label);
      case PropertyMatchOperator.AnsweredIncorrectly:
        return new AnsweredIncorrectlyOperator(label);
      case PropertyMatchOperator.NotAnswered:
        return new NotAnsweredOperator(label);
    }
  }
}
